import {
  applyVisibleFormFieldErrors,
  resolveValidationToastMessage,
} from '@/utils/validation/visibleFormFieldErrors';

import type { ZodError, ZodType } from 'zod';

export type AntdFieldError = {
  name: string | (string | number)[];
  errors: string[];
};

export type FieldNameToClear = string | (string | number)[];

export type ValidateWithSchemaResult<T = Record<string, unknown>> =
  { ok: true; data: T } | { ok: false; firstMessage: string };

const pathKey = (path: (string | number)[]): string =>
  path.length ? JSON.stringify(path) : '_form';

const fieldNamePath = (name: FieldNameToClear): (string | number)[] =>
  Array.isArray(name) ? name : [name];

/** True when `clearName` is a path-segment prefix of `name` (not a string prefix). */
export function fieldNameIsUnder(
  name: FieldNameToClear,
  clearName: FieldNameToClear,
): boolean {
  const namePath = fieldNamePath(name);
  const clearPath = fieldNamePath(clearName);
  if (clearPath.length === 0) {
    return true;
  }
  return clearPath.every((segment, index) => namePath[index] === segment);
}

type ExpandClearForm = {
  getFieldsError?: () => Array<{
    name: FieldNameToClear;
    errors: string[];
  }>;
};

export type FormFieldsErrorReader = ExpandClearForm;

/** Explicit clear names plus any currently errored fields nested under them. */
export function expandFieldNamesToClear(
  form: ExpandClearForm,
  names: FieldNameToClear[],
): AntdFieldError[] {
  const byPath = new Map<string, AntdFieldError>();

  const add = (name: FieldNameToClear) => {
    const key = pathKey(fieldNamePath(name));
    if (!byPath.has(key)) {
      byPath.set(key, { name, errors: [] });
    }
  };

  for (const name of names) {
    add(name);
  }

  const existing = form.getFieldsError?.() ?? [];
  for (const field of existing) {
    if (field.errors.length === 0) {
      continue;
    }
    if (names.some((clearName) => fieldNameIsUnder(field.name, clearName))) {
      add(field.name);
    }
  }

  return Array.from(byPath.values());
}

const normalizePath = (path: ReadonlyArray<PropertyKey>): (string | number)[] =>
  path.map((segment) =>
    typeof segment === 'symbol' ? String(segment) : segment,
  );

export const zodErrorToAntdFieldErrors = (
  error: ZodError,
): AntdFieldError[] => {
  const byPath = new Map<
    string,
    { name: (string | number)[]; errors: string[] }
  >();

  error.issues.forEach((issue) => {
    const name = issue.path.length ? normalizePath(issue.path) : ['_form'];
    const key = pathKey(name);
    const existing = byPath.get(key) ?? {
      name,
      errors: [],
    };
    if (!existing.errors.includes(issue.message)) {
      existing.errors.push(issue.message);
    }
    byPath.set(key, existing);
  });

  return Array.from(byPath.values()).map(({ name, errors }) => ({
    name: name.length === 1 && name[0] === '_form' ? '_form' : name,
    errors,
  }));
};

type ValidateForm = {
  setFields: (fields: AntdFieldError[]) => void;
  scrollToField: (
    name: string | (string | number)[],
    options?: { behavior?: ScrollBehavior; block?: ScrollLogicalPosition },
  ) => void;
  setFieldsValue?: (values: Record<string, unknown>) => void;
  getFieldInstance?: (name: string | (string | number)[]) => unknown;
  getFieldsError?: ExpandClearForm['getFieldsError'];
};

const DEFAULT_VALIDATION_FALLBACK = 'Validation failed';

export function validateWithSchema<T>(
  form: ValidateForm,
  schema: ZodType<T>,
  values: unknown,
  fieldNamesToClear?: FieldNameToClear[],
  formName?: string,
  options?: { fallbackMessage?: string },
): ValidateWithSchemaResult<T> {
  if (fieldNamesToClear?.length) {
    form.setFields(expandFieldNamesToClear(form, fieldNamesToClear));
  }

  const result = schema.safeParse(values);
  if (result.success) {
    const data = result.data as Record<string, unknown>;
    form.setFieldsValue?.(data);
    return { ok: true, data: result.data };
  }

  const fieldErrors = zodErrorToAntdFieldErrors(result.error);
  const { blocking, firstMessage } = applyVisibleFormFieldErrors(
    form,
    fieldErrors,
    { formName },
  );

  if (blocking.length === 0) {
    // Only unmounted/hidden fields failed — do not block the user.
    const data = values as T;
    if (
      values != null &&
      typeof values === 'object' &&
      !Array.isArray(values)
    ) {
      form.setFieldsValue?.(values as Record<string, unknown>);
    }
    return { ok: true, data };
  }

  const fallback = options?.fallbackMessage ?? DEFAULT_VALIDATION_FALLBACK;
  return {
    ok: false,
    firstMessage: resolveValidationToastMessage(firstMessage, fallback),
  };
}
